import { IColumn, IconButton, Stack, Text, TooltipHost } from '@fluentui/react';
import { _columns } from 'common/components/ActionMenu/Attach/_columns.data';
import { useStyles } from 'common/components/ActionMenu/Attach/AttachFormModal/index.styles';
import { DocumentDataCallout } from 'common/components/AttachDocumentModal/DocumentDataCallout';
import { DocumentViewModalState } from 'common/components/DocumentList';
import { TABLE_ROWS } from 'common/constants';
import { toOrderByVariable } from 'common/utils/actionMenuUtils';
import {
  dateConvertions,
  dateFormat,
  getGlobalDateFormat,
} from 'common/utils/dateFormats';
import React, { useCallback } from 'react';
import {
  AttachableBatchTransactionDocuments,
  AttachableBatchTransactionDocuments_attachableBatchTransactionDocuments_nodes,
} from '../../../../__generated__/AttachableBatchTransactionDocuments';
import { AttachBatchTransactionDocumentVariables } from './__generated__/AttachBatchTransactionDocument';
import { GenericAttachForm } from 'common/components/ActionMenu/Attach/AttachFormModal';
import { BatchTransactionAvailableDocumentTypes } from 'postingTracker/batchEdit/batch/view/__generated__/BatchTransactionAvailableDocumentTypes';
import { BatchTransaction_batchTransaction } from 'postingTracker/batchEdit/batch/view/__generated__/BatchTransaction';

import ATTACHABLE_BATCH_TRANSACTION_DOCUMENTS from '../../../../AttachableBatchTransactionDocuments.graphql';
import ATTACH_BATCH_TRANSACTION_DOCUMENT from './AttachBatchTransactionDocument.graphql';
import BATCH_TRANSACTION from '../../../../BatchTransaction.graphql';

type AttachableDataType =
  AttachableBatchTransactionDocuments_attachableBatchTransactionDocuments_nodes;
export interface AttachFormModalProps {
  availableDocumentTypes: BatchTransactionAvailableDocumentTypes | undefined;
  batchTransaction: BatchTransaction_batchTransaction;
  isDirty: boolean;
}
export const AttachFormModal: React.FC<AttachFormModalProps> = ({
  availableDocumentTypes,
  batchTransaction,
  isDirty,
}) => {
  const styles = useStyles();

  const getDocumentTypeOptions = useCallback(
    (data: typeof availableDocumentTypes | undefined) => {
      return (
        data?.batchTransactionAvailableDocumentTypes?.nodes.map((doctype) => ({
          key: doctype.id,
          text: doctype.documentType || '',
          title:
            !batchTransaction?.isDraft && doctype.isAccountingDocument
              ? 'Upload prohibited during/after the approval process'
              : undefined,
          disabled:
            !batchTransaction?.isDraft && doctype.isAccountingDocument
              ? true
              : undefined,
        })) || []
      );
    },
    [batchTransaction]
  );

  const canSelectItem = useCallback(
    (item: AttachableDataType) =>
      batchTransaction?.isDraft ? true : !item._isAccountingDocument!,
    [batchTransaction]
  );

  const transformData = useCallback(
    (data: AttachableBatchTransactionDocuments | undefined) => {
      return data?.attachableBatchTransactionDocuments?.nodes || [];
    },
    []
  );

  const getMutationInput = useCallback(
    (selectedItems: AttachableDataType[], entityId: string) => {
      const entityDocIds = selectedItems.map((item) => item.id);
      return {
        input: {
          batchTransactionId: entityId,
          entityDocumentId: entityDocIds,
        },
      } as AttachBatchTransactionDocumentVariables;
    },
    []
  );

  const onRenderItemColumn = useCallback(
    (
      item?: AttachableDataType,
      _index?: number,
      column?: IColumn,
      openDocumentViewModal?: (state: DocumentViewModalState) => void
    ) => {
      if (!item || !column) return undefined;

      const disableLogic = batchTransaction?.isDraft
        ? false
        : item._isAccountingDocument;

      const fieldContent = item[
        column.fieldName as keyof AttachableDataType
      ] as string | null;
      switch (column.key) {
        case 'fileReference':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <DocumentDataCallout disabled={disableLogic!} item={item} />
            </Stack>
          );
        case 'documentType':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {item.documentTypes?.documentType}
              </Text>
            </Stack>
          );
        case 'indexAmount':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <Text className={styles.contentColumnAlignRight}>
                {fieldContent}
              </Text>
            </Stack>
          );
        case 'isoCode':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {item.currency?.isoCode}
              </Text>
            </Stack>
          );
        case '_uploadDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {item._uploadDate ? getGlobalDateFormat(item._uploadDate) : ''}
              </Text>
            </Stack>
          );
        case 'indexTransactionDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              {item.indexTransactionDate && (
                <Text className={disableLogic ? styles.disabledText : ''}>
                  {dateFormat(dateConvertions(item.indexTransactionDate))}
                </Text>
              )}
            </Stack>
          );
        case 'view':
          const viewDocumentVisible =
            item._isProtected! || item._fileViewer !== 'browser';
          return (
            <Stack
              className={styles.columnHeight}
              tokens={{ childrenGap: 10 }}
              horizontal
              verticalAlign="center"
            >
              <TooltipHost content="View" id="tooltipId">
                <IconButton
                  disabled={viewDocumentVisible}
                  iconProps={{ iconName: 'View' }}
                  onClick={() =>
                    openDocumentViewModal?.({
                      isOpen: true,
                      title: item.fileReference,
                      entityDocumentId: item.id,
                      _fileType: item._fileType!,
                    })
                  }
                />
              </TooltipHost>
            </Stack>
          );
        default:
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {fieldContent}
              </Text>
            </Stack>
          );
      }
    },
    [styles, batchTransaction]
  );

  return (
    <GenericAttachForm
      entity={{
        id: batchTransaction.id!,
        isDraft: batchTransaction.isDraft,
      }}
      isDirty={isDirty}
      availableDocumentTypes={availableDocumentTypes}
      attachableDocumentsQuery={ATTACHABLE_BATCH_TRANSACTION_DOCUMENTS}
      attachDocumentsMutation={ATTACH_BATCH_TRANSACTION_DOCUMENT}
      detailsQuery={BATCH_TRANSACTION}
      columns={_columns}
      modalWidth={1350}
      getDocumentTypeOptions={getDocumentTypeOptions}
      transformData={transformData}
      canSelectItem={canSelectItem}
      getMutationInput={getMutationInput}
      onRenderItemColumn={onRenderItemColumn}
      initialQueryVariables={{
        batchTransactionId: batchTransaction.id!,
        first: TABLE_ROWS,
      }}
      toOrderByVariable={toOrderByVariable}
    />
  );
};
